import React, { useEffect, useState, useRef, createElement } from 'react';
import { CheckCircle2, Lock, User, Mail, Phone, Globe, X, ArrowRight, Clock, TrendingUp, DollarSign, Zap, Target, Users, Video, Calendar, Ticket, Search, Bot, MessageSquare, Mic, Quote, AlertTriangle, ShieldCheck, Car, Briefcase, HelpCircle, Frown, Sparkles, XCircle, Monitor, Gift, Headphones, Download, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../components/Footer';
declare global {
  interface Window {
    fbq: any;
  }
}
const COUNTRY_CODES = [{
  code: '+1',
  country: 'USA/Canada'
}, {
  code: '+52',
  country: 'México'
}, {
  code: '+57',
  country: 'Colombia'
}, {
  code: '+34',
  country: 'España'
}, {
  code: '+54',
  country: 'Argentina'
}, {
  code: '+56',
  country: 'Chile'
}, {
  code: '+51',
  country: 'Perú'
}, {
  code: '+593',
  country: 'Ecuador'
}, {
  code: '+58',
  country: 'Venezuela'
}, {
  code: '+502',
  country: 'Guatemala'
}, {
  code: '+503',
  country: 'El Salvador'
}, {
  code: '+504',
  country: 'Honduras'
}, {
  code: '+505',
  country: 'Nicaragua'
}, {
  code: '+506',
  country: 'Costa Rica'
}, {
  code: '+507',
  country: 'Panamá'
}, {
  code: '+591',
  country: 'Bolivia'
}, {
  code: '+595',
  country: 'Paraguay'
}, {
  code: '+598',
  country: 'Uruguay'
}, {
  code: '+1-809',
  country: 'Rep. Dominicana'
}];
// Get next Thursday 7PM Miami (EST) as Date object
const getNextThursdayDate = () => {
  const now = new Date();
  const estTime = new Date(now.toLocaleString('en-US', {
    timeZone: 'America/New_York'
  }));
  const currentDay = estTime.getDay();
  const currentHour = estTime.getHours();
  let daysUntilThursday = 0;
  if (currentDay === 4) {
    daysUntilThursday = currentHour >= 19 ? 7 : 0;
  } else if (currentDay < 4) {
    daysUntilThursday = 4 - currentDay;
  } else {
    daysUntilThursday = 7 - currentDay + 4;
  }
  const next = new Date(estTime);
  next.setDate(estTime.getDate() + daysUntilThursday);
  next.setHours(19, 0, 0, 0);
  // Convert back to actual moment (the est-string trick gave us a "wall clock" date in EST)
  // For the countdown we need actual ms remaining — use timezone offset correction
  const estOffsetMinutes = -new Date().toLocaleString('en-US', {
    timeZone: 'America/New_York',
    timeZoneName: 'short'
  }).includes('EDT') ? 240 : 300;
  // Simpler approach: compute target by anchoring to UTC
  const utcNow = new Date();
  // Find target date in EST then add EST offset
  // We'll compute the target by building an ISO string and parsing as if EST
  return next;
};
const getThursdayLabel = () => {
  const t = getNextThursdayDate();
  const day = t.getDate();
  const month = t.toLocaleString('es-ES', {
    month: 'long'
  }).toUpperCase();
  return `JUEVES ${day} DE ${month}`;
};
export function CarSalesmanWebinarPage() {
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [areaCode, setAreaCode] = useState('+1');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const navigate = useNavigate();
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    (function (f: any, b: any, e: any, v: any, n: any, t: any, s: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        b.head.appendChild(t);
      }
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', null, null, null);
    // Pixel base + init + PageView are handled globally in App.tsx.
    // Only fire this page's unique event to avoid double-counting.
    if (window.fbq) {
      window.fbq('trackCustom', 'CarSalesman-WebinarView');
    }
    // Countdown interval — re-anchor each tick using EST string
    const tick = () => {
      const nowMs = Date.now();
      // Build target: next Thursday at 7PM Miami in MS
      const estNow = new Date(new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York'
      }));
      const currentDay = estNow.getDay();
      const currentHour = estNow.getHours();
      let daysUntil = 0;
      if (currentDay === 4) {
        daysUntil = currentHour >= 19 ? 7 : 0;
      } else if (currentDay < 4) {
        daysUntil = 4 - currentDay;
      } else {
        daysUntil = 7 - currentDay + 4;
      }
      const estTarget = new Date(estNow);
      estTarget.setDate(estNow.getDate() + daysUntil);
      estTarget.setHours(19, 0, 0, 0);
      // Compute diff in EST domain — both timestamps are constructed from EST wall clocks
      let diffMs = estTarget.getTime() - estNow.getTime();
      if (diffMs < 0) diffMs = 0;
      const days = Math.floor(diffMs / 86400000);
      const hours = Math.floor(diffMs % 86400000 / 3600000);
      const minutes = Math.floor(diffMs % 3600000 / 60000);
      const seconds = Math.floor(diffMs % 60000 / 1000);
      setCountdown({
        days,
        hours,
        minutes,
        seconds
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const finalName = nameRef.current?.value?.trim() || name.trim();
    const finalEmail = emailRef.current?.value?.trim() || email.trim();
    const finalPhone = phoneRef.current?.value?.replace(/\D/g, '').trim() || phone.trim();
    if (!finalName || finalName.length < 2) {
      alert('Por favor ingresa tu nombre completo');
      return;
    }
    if (!finalEmail || !finalEmail.includes('@')) {
      alert('Por favor ingresa un email válido');
      return;
    }
    if (!finalPhone || finalPhone.length < 7) {
      alert('Por favor ingresa tu teléfono');
      return;
    }
    if (!smsConsent) {
      alert('Por favor marca la casilla de consentimiento de SMS para continuar.');
      return;
    }
    setIsSubmitting(true);
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', {
          content_name: 'Car Salesman Webinar Registration'
        });
      }
      const webhookUrl = 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/9e77fbea-dfb7-4800-9398-1f4c8701198d';
      await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: finalName,
          email: finalEmail,
          phone: `${areaCode}${finalPhone}`,
          first_name: finalName.split(' ')[0],
          last_name: finalName.split(' ').slice(1).join(' ') || finalName.split(' ')[0],
          full_name: finalName,
          fullPhone: `${areaCode}${finalPhone}`,
          areaCode: areaCode,
          source: '/car-salesman-webinar',
          page_path: '/car-salesman-webinar',
          tag: 'car-salesman-webinar',
          sms_consent: smsConsent,
          event_time: Math.floor(Date.now() / 1000),
          event_source_url: window.location.href,
          action_source: 'website'
        })
      });
      navigate('/thank-you');
    } catch (err) {
      console.error('Submit error:', err);
      navigate('/thank-you');
    } finally {
      setIsSubmitting(false);
    }
  };
  const painPoints = [{
    icon: Car,
    text: 'Las 70 horas semanales — sábados incluidos'
  }, {
    icon: DollarSign,
    text: 'La angustia de fin de mes — mes bueno vs mes malo'
  }, {
    icon: Frown,
    text: 'Los gerentes que ganan 3x más por hacer la mitad'
  }, {
    icon: Users,
    text: 'Perderte los partidos de tu hijo (otra vez)'
  }, {
    icon: TrendingUp,
    text: 'Las cuotas que suben cada año pero el sueldo no'
  }, {
    icon: Clock,
    text: 'La sensación de que sigues exactamente igual que hace 3 años'
  }];
  const blocks = [{
    icon: Search,
    label: 'Bloque 1',
    tag: 'Encontrar clientes',
    title: 'Cómo Encontrar 200 Clientes En 60 Segundos',
    desc: 'Voy a abrir LA HERRAMIENTA delante tuyo. En tiempo real. Vas a ver una lista de 200 dentistas / talleres / salones en CUALQUIER ciudad de USA — todos sin web. Listos para contactar.'
  }, {
    icon: Bot,
    label: 'Bloque 2',
    tag: 'Construir la web',
    title: 'Construyo Una Web Completa Delante Tuyo',
    desc: 'Sin código. Sin programación. En menos de 2 horas. Solo IA + supervisión + click. Vas a entender por qué cobramos $800–$1,500 por algo que toma 2 horas.'
  }, {
    icon: MessageSquare,
    label: 'Bloque 3',
    tag: 'Cerrar clientes',
    title: 'Los Mensajes Que Cierran',
    desc: 'Específicamente para vendedores de carros: cómo adaptas tu técnica de cierre del showroom a este nuevo contexto. Vas a ver pantallazos REALES de conversaciones donde un alumno cerró $1,200 con UN solo mensaje correcto.'
  }, {
    icon: Mic,
    label: 'Bloque 4',
    tag: 'Q&A',
    title: 'Q&A De 30 Minutos En Vivo',
    desc: 'Esto es lo que ningún video pre-grabado te puede dar. TÚ me preguntas. Sobre tu ciudad. Tu situación. Tus dudas. Te respondo en tiempo real.'
  }];
  const milestones = [{
    mes: 'Mes 1',
    value: '$3,600',
    note: '2 horas/día después del dealership'
  }, {
    mes: 'Mes 2',
    value: '$7,800',
    note: 'Primer cliente recurrente'
  }, {
    mes: 'Mes 3',
    value: '$11,500',
    note: 'Reemplazó su sueldo'
  }, {
    mes: 'Mes 4',
    value: '$14,200',
    note: 'RENUNCIÓ al dealership'
  }, {
    mes: 'Hoy (11 meses)',
    value: '$18K–$22K/mes',
    note: 'Promedio mensual',
    highlight: true
  }];
  const isFor = ['Eres vendedor de carros (o ex-vendedor con experiencia)', 'Vives en USA (cualquier estado)', 'Hablas español (o inglés básico)', 'Estás dispuesto a invertir 5–10 horas a la semana al modelo', 'Tienes mentalidad de EJECUTOR — no de "voy a pensarlo"', 'Estás cansado del schedule del dealership'];
  const notFor = ['Buscas un botón mágico que genere dinero sin esfuerzo', 'No tienes 5 horas a la semana', 'Eres "perpetuo aprendiz" que nunca ejecuta', 'Vienes solo a mirar webinars como entretenimiento'];
  const faqs = [{
    q: '¿Es para mí si NO soy vendedor de carros?',
    a: 'Es para CUALQUIER vendedor con experiencia previa: carros, seguros, bienes raíces, electrodomésticos. El webinar está optimizado para vendedores de carros porque son ideales — pero todos los que vendan algo, califican.'
  }, {
    q: '¿Tengo que tener mi cámara prendida?',
    a: 'NO. Es 100% asíncrono — puedes asistir desde el celular, con cámara apagada, hasta desde la cama. Solo necesitas estar conectado para no perderte la herramienta y el demo del software.'
  }, {
    q: '¿Habrá reposición si me lo pierdo?',
    a: 'NO. Es EN VIVO porque la herramienta y el demo no se pueden grabar (son en tiempo real). Si no puedes asistir el jueves, registrate igual — te aviso de los próximos webinars.'
  }, {
    q: '¿Cuánto cuesta el programa premium después?',
    a: 'Inversión inicial: $297. Acceso a todo el sistema, la comunidad, la herramienta de búsqueda, y soporte. Pero te lo explico AL FINAL del webinar con todos los detalles. No vienes a comprar — vienes a aprender.'
  }, {
    q: '¿50+ años? ¿Sigo a tiempo?',
    a: 'Mi mejor alumno del último trimestre tiene 58 años. Tu edad NO es factor. Tu experiencia EN VENTAS sí lo es. Si tienes 2+ años cerrando ventas, eres mejor candidato que un joven de 25 sin experiencia.'
  }];
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const thursdayLabel = getThursdayLabel();
  return <div className="min-h-screen bg-[#0a1428] text-white font-sans selection:bg-amber-400 selection:text-[#0a1428] overflow-x-hidden" data-id="element-949" data-mp="2xr08zq7y6rm2">
      <noscript data-id="element-950" data-mp="2sujjosp0i77e">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=754551913878347&ev=PageView&noscript=1" alt="" data-id="element-951" data-mp="2sqvnfls6n6oz" />
      </noscript>

      {/* Background Image Layer (same as /meta-jueves) */}
      <div className="absolute top-0 left-0 w-full h-[400px] md:h-[600px] z-0 opacity-60 pointer-events-none" data-id="element-952" data-mp="3ghhx1hhu4z13">
        <img src="https://cdn.magicpatterns.com/uploads/owSR6X5Pw8KuRfkif7sGzk/6936d7f7169a421d1ba8a294.png" alt="Background Pattern" className="w-full h-full object-cover" style={{
        maskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)'
      }} data-id="element-953" data-mp="3bi6dy1tzxhgc" />
      </div>

      {/* Ambient glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-amber-500/[0.08] blur-[140px] rounded-full pointer-events-none" data-id="element-954" data-mp="kkbrthvd658t" />
      <div className="absolute top-[40%] right-0 w-[500px] h-[500px] bg-amber-400/[0.04] blur-[120px] rounded-full pointer-events-none" data-id="element-955" data-mp="pid7dstkzmfj" />
      <div className="absolute top-[10%] left-0 w-[400px] h-[400px] bg-blue-500/[0.04] blur-[120px] rounded-full pointer-events-none" data-id="element-956" data-mp="zaj1b83e8dxd" />

      <div className="absolute inset-0 opacity-[0.015] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
      backgroundSize: '60px 60px'
    }} data-id="element-957" data-mp="13a04tvz9f0zw" />

      {/* ============ FLOATING HEADER ============ */}
      <header className="fixed top-3 md:top-5 left-0 right-0 z-50 px-3 md:px-6 pointer-events-none" data-id="element-958" data-mp="3f145eq1ixtxm">
        <div className="max-w-5xl mx-auto pointer-events-auto" data-id="element-959" data-mp="19cl9pl8ala6e">
          <div className="relative bg-[#0f1b35]/80 backdrop-blur-xl border border-amber-500/25 rounded-full shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6),0_0_30px_-10px_rgba(245,158,11,0.3)]" data-id="element-960" data-mp="19b0h415jg1b6">
            <div className="flex items-center justify-between gap-3 pl-3 pr-2 md:pl-5 md:pr-2.5 py-2 md:py-2.5" data-id="element-961" data-mp="1iczywvodn9f6">
              <div className="flex items-center gap-2.5 min-w-0" data-id="element-962" data-mp="1e9tt1p56fk98">
                <div className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full bg-amber-500/15 border border-amber-500/40 flex-shrink-0" data-id="element-963" data-mp="qn580ukvfboe">
                  <Video className="w-4 h-4 md:w-[18px] md:h-[18px] text-amber-400" data-id="element-964" data-mp="zef9d824wc3n" />
                </div>
                <div className="flex flex-col leading-none min-w-0" data-id="element-965" data-mp="uo763831xhos">
                  <span className="text-amber-400 text-[9px] md:text-[10px] font-black uppercase tracking-[0.18em] leading-none" data-id="element-966" data-mp="um49g57e8qb3">
                    Webinar Gratuito
                  </span>
                  <span className="text-white text-xs md:text-sm font-black tracking-tight mt-1 truncate" data-id="element-967" data-mp="2uoqqm8yjnq5">
                    Escapa del Dealership
                  </span>
                </div>
              </div>

              <div className="hidden lg:flex items-center gap-2 px-3" data-id="element-968" data-mp="3tqrt5gk2jdwj">
                <Calendar className="w-3.5 h-3.5 text-amber-400/70 flex-shrink-0" data-id="element-969" data-mp="3f1vrc18lux4d" />
                <span className="text-gray-300 text-xs font-bold uppercase tracking-[0.12em] leading-none whitespace-nowrap" data-id="element-970" data-mp="3jzxa0l24cufb">
                  {thursdayLabel}
                </span>
                <span className="inline-block w-px h-3.5 bg-amber-500/25" data-id="element-971" data-mp="ghdpk4pomsv2" />
                <Clock className="w-3.5 h-3.5 text-amber-400/70 flex-shrink-0" data-id="element-972" data-mp="1lehgu2kospx" />
                <span className="text-gray-300 text-xs font-bold uppercase tracking-[0.12em] leading-none whitespace-nowrap" data-id="element-973" data-mp="6k911y8d6lb3">
                  7PM EST
                </span>
              </div>

              <button type="button" onClick={() => setShowModal(true)} className="group flex items-center gap-1.5 md:gap-2 bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0a1428] font-black text-[10px] md:text-xs uppercase tracking-tight rounded-full px-3.5 md:px-5 py-2 md:py-2.5 shadow-[0_8px_25px_-8px_rgba(245,158,11,0.6)] hover:shadow-[0_8px_25px_-5px_rgba(245,158,11,0.8)] transition-all duration-300 flex-shrink-0 whitespace-nowrap" data-id="element-974" data-mp="2mdq497u2ww1d">
                <Ticket className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-975" data-mp="j0qw42189665" />
                <span className="hidden sm:inline" data-id="element-976" data-mp="e4thc05m47vh">Reservar Cupo</span>
                <span className="sm:hidden" data-id="element-977" data-mp="a1nb2sii2gp0">Reservar</span>
                <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 transition-transform group-hover:translate-x-0.5" data-id="element-978" data-mp="a49hupxkgs66" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 pt-20 md:pt-24 lg:pt-20 pb-12" data-id="element-979" data-mp="63h4bnhxs2w">
        {/* ============ HERO ============ */}
        <section className="mb-16 md:mb-20 lg:mb-28 text-center" data-id="element-980" data-mp="1uhi5ao0uzjgj">
          <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-3.5 py-1.5 mb-6 md:mb-8" data-id="element-981" data-mp="1ug76l2m9t1wa">
            <span className="relative flex h-2 w-2" data-id="element-982" data-mp="26zubjef6yi2h">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" data-id="element-983" data-mp="26y9g7ml1yv3k" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" data-id="element-984" data-mp="2gq5sc1jz4lqs" />
            </span>
            <span className="text-red-400 text-[10px] md:text-xs font-black uppercase tracking-[0.18em] leading-none" data-id="element-985" data-mp="1j2x0zpxakigv">
              Masterclass en Vivo
            </span>
          </div>

          {/* Hero visual — moved above headline */}
          <div className="relative flex justify-center mb-6 md:mb-8 lg:mb-10" data-id="element-986" data-mp="1t01lzmfei7w3">
            <div className="relative w-full max-w-2xl lg:max-w-4xl xl:max-w-5xl" data-id="element-987" data-mp="1y07iph0j2kl1">
              <div className="hidden md:flex absolute top-4 lg:top-8 left-0 lg:-left-4 xl:-left-10 z-20 bg-[#0f1b35]/95 backdrop-blur-sm border border-amber-500/30 rounded-2xl px-4 py-3 lg:px-5 lg:py-4 shadow-2xl rotate-[-6deg] hover:rotate-0 transition-transform duration-500" data-id="element-988" data-mp="34gja8z8uh47n">
                <div className="flex items-center gap-2.5 lg:gap-3" data-id="element-989" data-mp="3pk2akyjsbhl0">
                  <div className="w-9 h-9 lg:w-11 lg:h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center" data-id="element-990" data-mp="3plvfo9er1tqw">
                    <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-400" data-id="element-991" data-mp="3cc6msyqckdw" />
                  </div>
                  <div className="text-left" data-id="element-992" data-mp="h3h7fydu3ipt">
                    <div className="text-[10px] lg:text-[11px] text-gray-400 uppercase tracking-wider font-bold" data-id="element-993" data-mp="h6bqblh78vh4">
                      Ingreso meta
                    </div>
                    <div className="text-sm lg:text-base font-black text-white" data-id="element-994" data-mp="r1wn957ev5k0">
                      $10K–$20K/mes
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden md:flex absolute bottom-4 lg:bottom-8 right-0 lg:-right-4 xl:-right-10 z-20 bg-[#0f1b35]/95 backdrop-blur-sm border border-amber-500/30 rounded-2xl px-4 py-3 lg:px-5 lg:py-4 shadow-2xl rotate-[5deg] hover:rotate-0 transition-transform duration-500" data-id="element-995" data-mp="2subbsn4gn89e">
                <div className="flex items-center gap-2.5 lg:gap-3" data-id="element-996" data-mp="18afsmpdvt72g">
                  <div className="w-9 h-9 lg:w-11 lg:h-11 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center" data-id="element-997" data-mp="tne3y27g7e3t">
                    <Video className="w-4 h-4 lg:w-5 lg:h-5 text-amber-400" data-id="element-998" data-mp="yk4igl3lu1n4" />
                  </div>
                  <div className="text-left" data-id="element-999" data-mp="ko98x5v3i2we">
                    <div className="text-[10px] lg:text-[11px] text-gray-400 uppercase tracking-wider font-bold" data-id="element-1000" data-mp="plsp4bbcspqz">
                      Duración
                    </div>
                    <div className="text-sm lg:text-base font-black text-white" data-id="element-1001" data-mp="fn3en3lse7kr">
                      90 minutos
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute inset-0 bg-black/70 blur-[120px] lg:blur-[160px] rounded-full scale-90 pointer-events-none" data-id="element-1002" data-mp="2a14adfm1iegm" />

              <img src="https://cdn.magicpatterns.com/uploads/4WKjQ8RUKi5FvD9kSojqRR/Banner_Transparente_AI_WEB_CLOSER.png" alt="Gabriel Beltrán AI Web Closer Masterclass Live" className="relative w-full h-auto drop-shadow-2xl animate-fade-in-up" data-id="element-1003" data-mp="3dk7v1zxqk68a" />
            </div>
          </div>

          <h1 className="text-[2rem] md:text-5xl lg:text-[4.5rem] xl:text-[5rem] font-black leading-[1.02] tracking-tight mb-5 md:mb-6 lg:mb-7 max-w-5xl mx-auto" data-id="element-1004" data-mp="39j5sb94m1c1k">
            Escapa del Dealership y{' '}
            <span className="relative inline-block" data-id="element-1005" data-mp="55z5l0qifvkc">
              <span className="relative z-10 text-amber-400" data-id="element-1006" data-mp="53d0r8v07u5t">
                Genera $10K–$20K
              </span>
              <span className="absolute bottom-1 left-0 w-full h-3 bg-amber-400/15 -z-0 rounded-sm" data-id="element-1007" data-mp="3sa6ppsq30zj0" />
            </span>{' '}
            al Mes Desde Casa
          </h1>

          <div className="inline-flex flex-wrap items-center justify-center gap-2 md:gap-3 bg-white/[0.03] border border-amber-500/20 rounded-full px-4 md:px-5 py-2 md:py-2.5 mb-8 md:mb-10" data-id="element-1008" data-mp="2bulx6xohg10j">
            <Calendar className="w-4 h-4 text-amber-400" data-id="element-1009" data-mp="299xqrbmm503m" />
            <span className="text-amber-400 text-sm md:text-base font-black uppercase tracking-tight" data-id="element-1010" data-mp="24cxl31todzd1">
              {thursdayLabel}
            </span>
            <span className="w-px h-4 bg-amber-500/30" data-id="element-1011" data-mp="2ibdy1ai4sm3m" />
            <Clock className="w-4 h-4 text-amber-400" data-id="element-1012" data-mp="2dcuefgr0fmef" />
            <span className="text-amber-400 text-sm md:text-base font-black uppercase tracking-tight" data-id="element-1013" data-mp="2db9j3owvfzfi">
              7PM EST · Hora Miami
            </span>
          </div>

          {/* Mobile-only CTA — placed directly under the date badge */}
          <div className="md:hidden max-w-md mx-auto mb-8" data-id="element-1014" data-mp="1zecu7t0bcs19">
            <button type="button" onClick={() => setShowModal(true)} className="group relative w-full bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0a1428] font-black text-base uppercase tracking-tight rounded-2xl py-5 px-6 shadow-[0_20px_60px_-15px_rgba(245,158,11,0.6)] transition-all duration-300 transform hover:scale-[1.02] overflow-hidden" data-id="element-1015" data-mp="1uml0j9lv266w">
              <span className="relative z-10 flex flex-col items-center gap-1" data-id="element-1016" data-mp="31yc8lzjt6de0">
                <span className="flex items-center justify-center gap-2.5" data-id="element-1017" data-mp="36xf2qoy92z77">
                  <Ticket className="w-5 h-5" data-id="element-1018" data-mp="3brayrluyebzq" />
                  RESERVAR MI CUPO GRATIS
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-1019" data-mp="3go1hyi221npd" />
                </span>
                <span className="text-[10px] font-bold opacity-80 normal-case tracking-normal" data-id="element-1020" data-mp="2j35ef07tap0f">
                  Solo 100 cupos. Una vez lleno, se cierra el registro.
                </span>
              </span>
              <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-25deg] group-hover:animate-shine pointer-events-none" data-id="element-1021" data-mp="2t1l5x4ar40ag" />
            </button>

            <button type="button" onClick={() => setShowModal(true)} className="block w-full mt-4 rounded-2xl overflow-hidden border border-amber-500/30 hover:border-amber-400 transition-all duration-300 transform hover:scale-[1.01] shadow-[0_15px_40px_-15px_rgba(245,158,11,0.4)]" aria-label="Acceso incluido — Guía Gratis: La Trampa Del 9-5" data-id="element-1022" data-mp="cauuu8k1m57m">
              <img src="https://cdn.magicpatterns.com/uploads/2K7su451g6iG2yTT7FBywu/Guia_Gratis_Webinar_Page.png" alt="Acceso incluido — Guía Gratis: La Trampa Del 9-5" className="w-full h-auto block" data-id="element-1023" data-mp="1nzaizly4rl7e" />
            </button>
          </div>

          <p className="text-base md:text-lg lg:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto mb-8 md:mb-10 lg:mb-14" data-id="element-1024" data-mp="1e01gg4p78y9f">
            Masterclass en vivo donde te muestro el{' '}
            <span className="text-white font-semibold" data-id="element-1025" data-mp="151zhrvsxuhpb">modelo de negocio</span>{' '}
            que cientos de vendedores hispanos en USA están usando para
            reemplazar (y multiplicar) su sueldo del dealership —{' '}
            <span className="text-white font-semibold" data-id="element-1026" data-mp="2ugnujy5fy1nv">
              aplicando lo que ya sabes hacer.
            </span>
          </p>

          <div className="max-w-md lg:max-w-xl mx-auto" data-id="element-1027" data-mp="1r6tbca8os89m">
            <button type="button" onClick={() => setShowModal(true)} className="group relative w-full bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0a1428] font-black text-base md:text-lg lg:text-xl uppercase tracking-tight rounded-2xl py-5 md:py-6 lg:py-7 px-6 shadow-[0_20px_60px_-15px_rgba(245,158,11,0.6)] hover:shadow-[0_20px_60px_-10px_rgba(245,158,11,0.75)] transition-all duration-300 transform hover:scale-[1.02] overflow-hidden hidden md:block" data-id="element-1028" data-mp="1w4uqwl6wlpgc">
              <span className="relative z-10 flex flex-col items-center gap-1" data-id="element-1029" data-mp="zekhzf867w19">
                <span className="flex items-center justify-center gap-2.5 lg:gap-3" data-id="element-1030" data-mp="zchim4l4oqjv">
                  <Ticket className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7" data-id="element-1031" data-mp="13gfe85b8vj86" />
                  RESERVAR MI CUPO GRATIS
                  <ArrowRight className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 transition-transform group-hover:translate-x-1" data-id="element-1032" data-mp="piqux1wtsk58" />
                </span>
                <span className="text-[10px] md:text-xs font-bold opacity-80 normal-case tracking-normal" data-id="element-1033" data-mp="auqquy1b0v6o">
                  Solo 100 cupos. Una vez lleno, se cierra el registro.
                </span>
              </span>
              <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-25deg] group-hover:animate-shine pointer-events-none" data-id="element-1034" data-mp="ktg9it10yjnz" />
            </button>

            <div className="mt-5 lg:mt-6 grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-2.5" data-id="element-1035" data-mp="tqgz32ztynt0">
              {['100% gratis', 'En vivo por Zoom', 'Q&A en directo', 'Regalo por asistir'].map(item => <div key={item} className="flex items-center justify-center lg:justify-start gap-1.5 bg-white/[0.03] border border-white/[0.06] rounded-full px-3 py-1.5" data-id="element-1036" data-mp="13hla5rmdxoik">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" data-id="element-1037" data-mp="1ck2we621ih5k" />
                  <span className="text-[11px] md:text-xs text-gray-400 font-medium truncate" data-id="element-1038" data-mp="18i916dam65cs">
                    {item}
                  </span>
                </div>)}
            </div>
          </div>
        </section>

        {/* ============ SECTION 2 — Si Estás Leyendo Esto ============ */}
        <section className="mb-16 md:mb-20 lg:mb-28" data-id="element-1039" data-mp="1pik7p7f6ir4l">
          <div className="max-w-4xl mx-auto" data-id="element-1040" data-mp="1pgqzvosty0v0">
            <div className="text-center mb-10 lg:mb-12" data-id="element-1041" data-mp="1u9sfy04rw6g6">
              <div className="inline-block text-amber-400 text-xs md:text-sm font-black uppercase tracking-[0.2em] mb-3" data-id="element-1042" data-mp="1ubddlymdw5i9">
                Sé Por Qué Estás Aquí
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight" data-id="element-1043" data-mp="238dxpt2f7hfw">
                Si Estás Leyendo Esto,{' '}
                <span className="text-amber-400" data-id="element-1044" data-mp="2d8em3tf5cnqy">Probablemente...</span>
              </h2>
            </div>

            <div className="space-y-5 text-gray-300 text-base md:text-lg lg:text-[19px] leading-relaxed max-w-3xl mx-auto" data-id="element-1045" data-mp="dvk9rg4b89qp">
              <p data-id="element-1046" data-mp="ivevz6d54b5f">Llevas años vendiendo carros en el dealership.</p>
              <p data-id="element-1047" data-mp="2w4ercibdj2d2">
                Eres bueno. Cumples cuotas. A veces top 5 del dealership. Otras
                veces te toca el mes malo.
              </p>
              <p className="text-white font-semibold" data-id="element-1048" data-mp="2rcn9cw6aa4yj">
                Pero algo en ti dice "esto no es para siempre".
              </p>
              <p data-id="element-1049" data-mp="2idicduadejoh">Estás cansado de:</p>
            </div>

            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4 max-w-4xl mx-auto" data-id="element-1050" data-mp="2ietgxwbn3vgn">
              {painPoints.map(p => <li key={p.text} className="flex items-start gap-3 bg-red-950/20 border border-red-500/15 rounded-xl px-4 py-3.5 lg:px-5 lg:py-4" data-id="element-1051" data-mp="3f1esky1wbcz6">
                  <div className="w-9 h-9 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0" data-id="element-1052" data-mp="3621hk0y4igcc">
                    <p.icon className="w-4 h-4 lg:w-[18px] lg:h-[18px] text-red-400" data-id="element-1053" data-mp="315ucsbhgravz" />
                  </div>
                  <span className="text-sm md:text-base lg:text-[17px] text-gray-200 font-medium leading-snug" data-id="element-1054" data-mp="1ulb9sde1xggm">
                    {p.text}
                  </span>
                </li>)}
            </ul>

            <p className="mt-10 text-center text-amber-400 text-lg md:text-xl lg:text-2xl font-black leading-tight max-w-2xl mx-auto" data-id="element-1055" data-mp="3pt6vsjonme4x">
              Si te sientes identificado — esta masterclass es para ti.
            </p>
          </div>
        </section>

        {/* ============ SECTION 3 — Qué Vas A Ver EN VIVO ============ */}
        <section className="mb-16 md:mb-20 lg:mb-28" data-id="element-1056" data-mp="cbee91m22hu3">
          <div className="text-center mb-10 md:mb-14 lg:mb-16" data-id="element-1057" data-mp="ce0stl7d5xlt">
            <div className="inline-block text-amber-400 text-xs md:text-sm font-black uppercase tracking-[0.2em] mb-3" data-id="element-1058" data-mp="h9pkz3h20c83">
              La Agenda
            </div>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight max-w-3xl mx-auto" data-id="element-1059" data-mp="7kxz9d2l6o5m">
              En 90 Minutos <span className="text-amber-400" data-id="element-1060" data-mp="vajtrn80kf7a">Vas A Ver:</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6 max-w-5xl mx-auto" data-id="element-1061" data-mp="101s4qnozs8mk">
            {blocks.map((b, idx) => <div key={b.title} className="group relative bg-white/[0.025] border border-white/[0.08] rounded-2xl p-6 md:p-7 lg:p-8 hover:border-amber-500/40 hover:bg-white/[0.04] transition-all duration-300" data-id="element-1062" data-mp="2kqcprhand4m9" data-mpi={idx}>
                <div className="flex items-start gap-4" data-id="element-1063" data-mp="2ugxkuur43r0f">
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0" data-id="element-1064" data-mp="2pidwyosyk55f">
                    <b.icon className="w-5 h-5 lg:w-6 lg:h-6 text-amber-400" data-id="element-1065" data-mp="21uwan8sgpz9u" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1066" data-mp="2atrjksmw5ngu">
                    <div className="flex items-center gap-2 mb-2" data-id="element-1067" data-mp="2auj2p3zaxsq8">
                      <span className="text-amber-400 text-[10px] lg:text-xs font-black uppercase tracking-[0.18em]" data-id="element-1068" data-mp="26rmtu4zkdazh">
                        {b.label}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-amber-500/40" data-id="element-1069" data-mp="3i7beuqt9bzxn" />
                      <span className="text-gray-400 text-[10px] lg:text-xs font-bold uppercase tracking-wider" data-id="element-1070" data-mp="3i9e2j46xtgz7">
                        {b.tag}
                      </span>
                    </div>
                    <h3 className="text-base md:text-lg lg:text-xl font-black text-white mb-2 leading-snug" data-id="element-1071" data-mp="3rww2sm9mr1kd">
                      {b.title}
                    </h3>
                    <p className="text-sm md:text-[15px] lg:text-base text-gray-400 leading-relaxed" data-id="element-1072" data-mp="3hxx15nna866v">
                      {b.desc}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          <div className="mt-10 lg:mt-12 max-w-md mx-auto" data-id="element-1073" data-mp="ehzn33m02e39">
            <button type="button" onClick={() => setShowModal(true)} className="group w-full flex items-center justify-center gap-2.5 bg-white/[0.04] border border-amber-500/30 hover:bg-amber-500/10 hover:border-amber-400 text-white font-black text-sm md:text-base uppercase tracking-tight rounded-xl py-3.5 md:py-4 px-6 transition-all duration-300" data-id="element-1074" data-mp="9nvi2pwaog4t">
              <Ticket className="w-4 h-4 md:w-5 md:h-5 text-amber-400" data-id="element-1075" data-mp="15nes040e5re4" />
              SÍ, QUIERO ASISTIR AL WEBINAR
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-amber-400 transition-transform group-hover:translate-x-1" data-id="element-1076" data-mp="19ogr8kub63g3" />
            </button>
          </div>
        </section>

        {/* ============ SECTION 4 — Caso Real Andrés ============ */}
        <section className="mb-16 md:mb-20 lg:mb-28" data-id="element-1077" data-mp="1johfml71b9r5">
          <div className="max-w-5xl mx-auto" data-id="element-1078" data-mp="1omsq8y55loaf">
            <div className="text-center mb-10 lg:mb-12" data-id="element-1079" data-mp="1om0thjztlkik">
              <div className="inline-block text-amber-400 text-xs md:text-sm font-black uppercase tracking-[0.2em] mb-3" data-id="element-1080" data-mp="1sn2sbzptruk0">
                Caso Real
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-[1.1] max-w-4xl mx-auto" data-id="element-1081" data-mp="22do0oevawdg4">
                Andrés Era Vendedor De Toyota En Phoenix. Hoy Gana{' '}
                <span className="text-amber-400" data-id="element-1082" data-mp="27anxdz69g3uk">$18K/Mes</span> Desde Casa.
              </h2>
            </div>

            <div className="bg-gradient-to-br from-amber-950/20 to-[#0a1428] border border-amber-500/20 rounded-3xl p-6 md:p-10 lg:p-12" data-id="element-1083" data-mp="2c9h8xm1zlks3">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 mb-10 lg:mb-12" data-id="element-1084" data-mp="1hpegks7sng6a">
                <div className="lg:col-span-3 space-y-4 text-gray-300 text-base lg:text-lg leading-relaxed" data-id="element-1085" data-mp="1wdrbrmz24uzq">
                  <p data-id="element-1086" data-mp="1rlri2c9kyybl">
                    Andrés tiene 38 años. 8 años vendiendo carros en Toyota
                    Phoenix. Top 5 del dealership casi siempre.
                  </p>
                  <p data-id="element-1087" data-mp="20fw4mjholnlb">
                    Pero llegaba a casa a las 9:30 PM. Sus hijos crecían sin él.
                    Su esposa Laura le dijo que Sebastián (12 años) le había
                    preguntado si su papá era{' '}
                    <span className="text-white font-semibold" data-id="element-1088" data-mp="2adtsv6zjjkdx">"real"</span> o
                    solo aparecía para Navidad.
                  </p>
                  <p className="text-white font-semibold" data-id="element-1089" data-mp="fzj22szdyg5i">
                    Esa noche, Andrés no durmió.
                  </p>
                  <p data-id="element-1090" data-mp="2rnnw6acrzzfk">
                    A las 3 AM estaba en su laptop buscando alternativas.
                    Encontró uno de mis webinars.
                  </p>
                </div>

                {/* Pull quote */}
                <div className="lg:col-span-2 relative bg-white/[0.04] border border-amber-500/30 rounded-2xl p-6 lg:p-7" data-id="element-1091" data-mp="2hwa06ck19hr6">
                  <Quote className="w-7 h-7 text-amber-400/60 mb-3" data-id="element-1092" data-mp="2mtjofovorndf" />
                  <p className="text-white text-base lg:text-lg font-medium italic leading-snug mb-4" data-id="element-1093" data-mp="28uk1qmrqhijw">
                    "Lo único que me detenía antes era no saber CÓMO. El día que
                    vi este webinar, dejé de tener excusa."
                  </p>
                  <div className="flex items-center gap-2.5" data-id="element-1094" data-mp="23w8qq8ppd203">
                    <div className="w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center" data-id="element-1095" data-mp="23yv8f16b4xw1">
                      <User className="w-4 h-4 text-amber-400" data-id="element-1096" data-mp="25xaiqxobf48" />
                    </div>
                    <div data-id="element-1097" data-mp="1y9bthylh8am2">
                      <div className="text-white font-black text-sm" data-id="element-1098" data-mp="1y7ikwdrazgbf">
                        Andrés
                      </div>
                      <div className="text-gray-400 text-[11px] uppercase tracking-wider font-bold" data-id="element-1099" data-mp="1t7mg4cas7kut">
                        Ex-vendedor Toyota Phoenix
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Milestones timeline */}
              <div data-id="element-1100" data-mp="1fbsi4objqfwh">
                <div className="text-amber-400 text-xs lg:text-sm font-black uppercase tracking-[0.2em] mb-5" data-id="element-1101" data-mp="1ajhfhdpf8g08">
                  Los Resultados
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4" data-id="element-1102" data-mp="15i1wdr6t0jkh">
                  {milestones.map(m => <div key={m.mes} className={`relative rounded-2xl p-4 lg:p-5 border transition-all ${m.highlight ? 'bg-amber-500/10 border-amber-500/50 shadow-[0_0_30px_rgba(245,158,11,0.15)]' : 'bg-white/[0.03] border-white/[0.08]'}`} data-id="element-1103" data-mp="wjggk3spvine">
                      <div className={`text-[10px] lg:text-xs font-black uppercase tracking-[0.18em] mb-1.5 ${m.highlight ? 'text-amber-400' : 'text-gray-400'}`} data-id="element-1104" data-mp="336njwk716zkc">
                        {m.mes}
                      </div>
                      <div className={`text-lg lg:text-2xl font-black mb-1 ${m.highlight ? 'text-amber-400' : 'text-white'}`} data-id="element-1105" data-mp="3d6eh4tmz7so4">
                        {m.value}
                      </div>
                      <div className="text-[11px] lg:text-xs text-gray-400 leading-snug" data-id="element-1106" data-mp="2kiin5woskev1">
                        {m.note}
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ SECTION 4.5 — BONUSES (Book + Meditations) ============ */}
        <section className="mb-16 md:mb-20 lg:mb-28" data-id="element-1107" data-mp="21qzi7kb7iqqd">
          <div className="max-w-5xl mx-auto" data-id="element-1108" data-mp="21q7vkyzl83c8">
            <div className="text-center mb-10 lg:mb-12" data-id="element-1109" data-mp="25s2z8a4zqst6">
              <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 mb-4" data-id="element-1110" data-mp="25qi3wiaur5u9">
                <Gift className="w-3.5 h-3.5 text-amber-400" data-id="element-1111" data-mp="344f8c4bzs0xu" />
                <span className="text-amber-400 text-[11px] md:text-xs font-black uppercase tracking-[0.18em] leading-none" data-id="element-1112" data-mp="343vqsoq97ekc">
                  Regalos Exclusivos Por Asistir
                </span>
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight max-w-4xl mx-auto" data-id="element-1113" data-mp="3dwb8dkxn214p">
                Cuando Te Registras <span className="text-amber-400" data-id="element-1114" data-mp="2l5t78pgh69tx">Hoy</span>,
                También Recibes:
              </h2>
              <p className="mt-4 text-gray-400 text-base md:text-lg max-w-2xl mx-auto" data-id="element-1115" data-mp="2z5b0bh0t0993">
                Dos bonus diseñados para que llegues al webinar 10x mejor
                preparado — y aproveches cada minuto al máximo.
              </p>
            </div>

            {/* ========= BONUS 1 — BOOK ========= */}
            <div className="relative bg-gradient-to-br from-amber-950/20 to-[#0a1428] border border-amber-500/30 rounded-3xl p-5 md:p-8 lg:p-10 mb-6 md:mb-8 overflow-hidden" data-id="element-1116" data-mp="acgkx6zkyvue">
              <div className="absolute -top-20 -right-20 w-60 h-60 bg-amber-400/5 blur-3xl rounded-full pointer-events-none" data-id="element-1117" data-mp="mwdn9rfvllgb" />
              <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8 items-center" data-id="element-1118" data-mp="7zv5toh6m58r">
                {/* Book image */}
                <div className="lg:col-span-2" data-id="element-1119" data-mp="czhju04u3pim">
                  <div className="relative rounded-2xl overflow-hidden border-2 border-amber-500/20 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]" data-id="element-1120" data-mp="10oaabh2ot55u">
                    <img src="https://cdn.magicpatterns.com/uploads/3koR9Um6Z3JLvhRxNTnoti/WEBINAR_BONUS_GUIDE_BOOK.png" alt="Guía Gratis - La Trampa Del 9-5" className="w-full block" data-id="element-1121" data-mp="15o6dxf7h2ukv" />
                  </div>
                </div>

                {/* Content */}
                <div className="lg:col-span-3" data-id="element-1122" data-mp="1jlbgv6bw3dbw">
                  <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1 mb-4" data-id="element-1123" data-mp="1octi7vhtrfxe">
                    <BookOpen className="w-3.5 h-3.5 text-amber-400" data-id="element-1124" data-mp="3kgzto53xht1a" />
                    <span className="text-amber-400 text-[10px] md:text-xs font-black uppercase tracking-[0.18em] leading-none" data-id="element-1125" data-mp="3fpht3i5thugu">
                      Bonus #1 · Guía Gratis
                    </span>
                  </div>

                  <h3 className="text-xl md:text-2xl lg:text-3xl font-black text-white leading-tight mb-2.5" data-id="element-1126" data-mp="36pmet4gytjcl">
                    Descarga La Guía Gratis Para{' '}
                    <span className="text-amber-400" data-id="element-1127" data-mp="37uorvme39h1">Prepararte</span>
                  </h3>

                  <p className="text-sm md:text-base text-amber-400 font-bold mb-4" data-id="element-1128" data-mp="3ue5mbhs6xs3m">
                    Léela ANTES de la Masterclass para llegar 10x mejor
                    preparado
                  </p>

                  <p className="text-sm md:text-base text-gray-300 leading-relaxed mb-5" data-id="element-1129" data-mp="2iypdsmqpk6g4">
                    Esta guía de{' '}
                    <span className="text-white font-bold" data-id="element-1130" data-mp="2dyujsabmz0qu">30 páginas</span> te
                    va a dar el{' '}
                    <span className="text-white font-bold" data-id="element-1131" data-mp="23xij19d5tzsh">CONTEXTO</span> que
                    vas a necesitar para aprovechar al máximo la Masterclass del
                    jueves.
                  </p>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-5 text-[11px] md:text-xs text-gray-400" data-id="element-1132" data-mp="2dstyjtwe7b1a">
                    <span className="flex items-center gap-1.5" data-id="element-1133" data-mp="2imohgv46blzb">
                      <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" data-id="element-1134" data-mp="2mlx7pq0091q4" /> 30
                      páginas
                    </span>
                    <span className="flex items-center gap-1.5" data-id="element-1135" data-mp="2rncrleqg526x">
                      <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" data-id="element-1136" data-mp="2wl5y68vnvy7q" />{' '}
                      PDF
                    </span>
                    <span className="flex items-center gap-1.5" data-id="element-1137" data-mp="126likam76sv7">
                      <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" data-id="element-1138" data-mp="1286g893t6rxa" />{' '}
                      Descarga inmediata
                    </span>
                    <span className="flex items-center gap-1.5" data-id="element-1139" data-mp="718dboosjif9">
                      <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" data-id="element-1140" data-mp="732padi34077" /> En
                      español
                    </span>
                  </div>

                  <button type="button" onClick={() => setShowModal(true)} className="group inline-flex items-center justify-center gap-2.5 bg-white/[0.04] border border-amber-500/30 hover:bg-amber-500/10 hover:border-amber-400 text-white font-black text-xs md:text-sm uppercase tracking-tight rounded-xl py-3 px-5 transition-all duration-300" data-id="element-1141" data-mp="g2pu79a0vo50">
                    <Download className="w-4 h-4 text-amber-400" data-id="element-1142" data-mp="3of3sicfdvagf" />
                    Recibir La Guía Al Registrarme
                    <ArrowRight className="w-4 h-4 text-amber-400 transition-transform group-hover:translate-x-1" data-id="element-1143" data-mp="21lriquwctuu" />
                  </button>
                </div>
              </div>
            </div>

            {/* ========= BONUS 2 — MONROE CODE MEDITATIONS ========= */}
            <div className="relative bg-gradient-to-br from-amber-400 to-amber-500 rounded-3xl p-5 md:p-8 lg:p-12 text-[#0a1428] overflow-hidden shadow-[0_30px_80px_-30px_rgba(245,158,11,0.55)]" data-id="element-1144" data-mp="35ovta5et145i">
              <div className="absolute -top-10 -right-10 w-40 h-40 md:w-60 md:h-60 bg-white/10 rounded-full blur-3xl pointer-events-none" data-id="element-1145" data-mp="2t69xttn942ex" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 md:w-60 md:h-60 bg-[#0a1428]/10 rounded-full blur-3xl pointer-events-none" data-id="element-1146" data-mp="2k87rqzonujks" />

              <div className="relative" data-id="element-1147" data-mp="2f9wior66wd3m">
                <div className="inline-flex items-center gap-2 bg-[#0a1428] text-amber-400 rounded-full px-3 md:px-4 py-1 md:py-1.5 mb-4 md:mb-5" data-id="element-1148" data-mp="2ai4zj1pd59ni">
                  <Gift className="w-3 h-3 md:w-3.5 md:h-3.5" data-id="element-1149" data-mp="25jbjb1ioz4jn" />
                  <span className="text-[10px] md:text-xs font-black uppercase tracking-[0.18em] leading-none" data-id="element-1150" data-mp="20kgsbcakm9og">
                    Bonus #2 · Exclusivo Por Asistir En Vivo
                  </span>
                </div>

                <h3 className="text-xl md:text-3xl lg:text-5xl font-black leading-[1.05] tracking-tight mb-2 md:mb-3" data-id="element-1151" data-mp="1rkbxs2qh75rp">
                  El Código Monroe En Español
                </h3>
                <p className="text-base md:text-xl lg:text-2xl text-[#0a1428]/80 font-black leading-tight mb-4 md:mb-5" data-id="element-1152" data-mp="1lx8lsos7isit">
                  Las 5 Meditaciones Binaurales Que La CIA Usó Por 30 Años
                </p>

                <p className="text-sm md:text-base lg:text-lg font-bold text-[#0a1428]/85 mb-4 md:mb-5 max-w-2xl" data-id="element-1153" data-mp="1cx3hinib8a8v">
                  Valor:{' '}
                  <span className="line-through opacity-60" data-id="element-1154" data-mp="25kzl8c6ar2f5">$297 USD</span> —{' '}
                  <span className="bg-[#0a1428] text-amber-400 px-2 py-0.5 rounded" data-id="element-1155" data-mp="25n27qm88qdf4">
                    GRATIS
                  </span>{' '}
                  solo si asistes EN VIVO al webinar.
                </p>

                <p className="text-sm md:text-base text-[#0a1428]/85 leading-relaxed mb-5 md:mb-6 max-w-2xl" data-id="element-1156" data-mp="2jkqxa7d968qi">
                  Las únicas meditaciones binaurales en español basadas en la
                  tecnología <span className="font-black" data-id="element-1157" data-mp="cwi42gd9k0rs">Hemi-Sync</span>{' '}
                  desarrollada por Bob Monroe — utilizada por la{' '}
                  <span className="font-black" data-id="element-1158" data-mp="7wcb8wv9c884">
                    CIA en su programa Stargate
                  </span>{' '}
                  para entrenar agentes operativos.
                  <span className="block mt-3" data-id="element-1159" data-mp="hmosog7rzla1">
                    Recreadas en español específicamente para los{' '}
                    <span className="font-black" data-id="element-1160" data-mp="3q41nwh846zqr">5 momentos críticos</span> del
                    journey de salir del 9-5:
                  </span>
                </p>

                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-2.5 mb-5 md:mb-6" data-id="element-1161" data-mp="2y2fak1pl3lg">
                  {['Audio 1 — Frecuencia Del Primer Movimiento', 'Audio 2 — Inmunidad Operativa', 'Audio 3 — Identidad Trascendente', 'Audio 4 — Visualización Pre-Operación', 'Audio 5 — Activación Energética'].map(a => <li key={a} className="flex items-center gap-2.5 md:gap-3 bg-[#0a1428]/10 backdrop-blur-sm rounded-xl px-3 md:px-4 py-2.5 md:py-3" data-id="element-1162" data-mp="19emrwhgaxyuc">
                      <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-[#0a1428] flex items-center justify-center flex-shrink-0" data-id="element-1163" data-mp="24q4600585ydb">
                        <Headphones className="w-3.5 h-3.5 md:w-4 md:h-4 text-amber-400" data-id="element-1164" data-mp="2emqnqg2989b4" />
                      </div>
                      <span className="text-[13px] md:text-sm lg:text-base text-[#0a1428] font-bold leading-snug" data-id="element-1165" data-mp="2jmd1cqlzvrkg">
                        {a}
                      </span>
                    </li>)}
                </ul>

                <div className="flex items-start gap-2.5 md:gap-3 bg-[#0a1428] text-amber-100 rounded-xl px-3 md:px-4 py-3 md:py-3.5" data-id="element-1166" data-mp="2slqgnzwsvad3">
                  <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-1167" data-mp="2xe08xmle0mij" />
                  <p className="text-[12px] md:text-sm leading-snug" data-id="element-1168" data-mp="32ee80f50gm4f">
                    <span className="font-black text-amber-400" data-id="element-1169" data-mp="37cph2nnhesll">
                      NO se venden por separado.
                    </span>{' '}
                    Solo se entregan EN VIVO al final del webinar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ SECTION 5 — Detalles + Countdown ============ */}
        <section className="mb-16 md:mb-20 lg:mb-28" data-id="element-1170" data-mp="qpranh6jjocy">
          <div className="max-w-4xl mx-auto" data-id="element-1171" data-mp="lq4vv3b2e021">
            <div className="text-center mb-10 lg:mb-12" data-id="element-1172" data-mp="lr6h20lso2xh">
              <div className="inline-block text-amber-400 text-xs md:text-sm font-black uppercase tracking-[0.2em] mb-3" data-id="element-1173" data-mp="1jddnlg1ns5ch">
                Reserva Tu Cupo
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight" data-id="element-1174" data-mp="1egwoxrwu28l5">
                Los <span className="text-amber-400" data-id="element-1175" data-mp="19lr30t3fktx2">Detalles</span>:
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6 mb-8 lg:mb-10" data-id="element-1176" data-mp="14mmtg1clgu67">
              {[{
              icon: Calendar,
              label: 'Fecha',
              value: thursdayLabel
            }, {
              icon: Clock,
              label: 'Hora',
              value: '7:00 PM hora Miami (EST)'
            }, {
              icon: Video,
              label: 'Duración',
              value: '90 min + 30 min Q&A'
            }, {
              icon: Monitor,
              label: 'Plataforma',
              value: 'Zoom'
            }, {
              icon: DollarSign,
              label: 'Inversión',
              value: '100% GRATIS',
              highlight: true
            }, {
              icon: AlertTriangle,
              label: 'Importante',
              value: 'Es EN VIVO — NO hay reposición',
              warning: true
            }].map(d => <div key={d.label} className={`flex items-start gap-3 rounded-2xl p-5 lg:p-6 border ${d.warning ? 'bg-red-950/20 border-red-500/30' : d.highlight ? 'bg-amber-500/10 border-amber-500/40 shadow-[0_0_30px_rgba(245,158,11,0.12)]' : 'bg-white/[0.03] border-white/[0.08]'}`} data-id="element-1177" data-mp="2yyfkowyvx77n">
                  <div className={`w-11 h-11 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center flex-shrink-0 border ${d.warning ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/30'}`} data-id="element-1178" data-mp="123syl303w90p">
                    <d.icon className={`w-5 h-5 ${d.warning ? 'text-red-400' : 'text-amber-400'}`} data-id="element-1179" data-mp="177253svsoivq" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1180" data-mp="1g9uyc1ojf4o7">
                    <div className="text-[10px] lg:text-xs font-black uppercase tracking-[0.18em] text-gray-400 mb-1" data-id="element-1181" data-mp="1g8a287mkrdo8">
                      {d.label}
                    </div>
                    <div className={`font-black text-base lg:text-lg ${d.warning ? 'text-red-400' : d.highlight ? 'text-amber-400' : 'text-white'}`} data-id="element-1182" data-mp="1q0fvrlsh635j">
                      {d.value}
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Countdown */}
            <div className="relative bg-gradient-to-br from-[#0f1b35] to-[#0a1428] border-2 border-amber-500/40 rounded-3xl p-6 md:p-10 lg:p-12 text-center shadow-[0_0_60px_rgba(245,158,11,0.15)] overflow-hidden" data-id="element-1183" data-mp="9kmqcyur5zbv">
              <div className="absolute top-0 right-0 w-40 h-40 bg-amber-400/10 blur-3xl rounded-full" data-id="element-1184" data-mp="u3y9nv7pz9bu" />
              <div className="relative" data-id="element-1185" data-mp="z29ic2ma3dsh">
                <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 mb-5" data-id="element-1186" data-mp="13uj3l5cg7pof">
                  <Clock className="w-3.5 h-3.5 text-amber-400 animate-pulse" data-id="element-1187" data-mp="17v2z64hdil8g" />
                  <span className="text-amber-400 text-[11px] md:text-xs font-black uppercase tracking-widest" data-id="element-1188" data-mp="b6eusaykywhi">
                    El Webinar Empieza En
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-2 md:gap-4 max-w-2xl mx-auto mb-7" data-id="element-1189" data-mp="k61yj3es8edq">
                  {[{
                  label: 'Días',
                  value: countdown.days
                }, {
                  label: 'Horas',
                  value: countdown.hours
                }, {
                  label: 'Min',
                  value: countdown.minutes
                }, {
                  label: 'Seg',
                  value: countdown.seconds
                }].map(u => <div key={u.label} className="bg-[#0a1428] border border-amber-500/20 rounded-xl py-4 md:py-6 px-2 md:px-4" data-id="element-1190" data-mp="25px9sw4tofaj">
                      <div className="text-3xl md:text-5xl lg:text-6xl font-black text-amber-400 tabular-nums leading-none mb-1.5" data-id="element-1191" data-mp="20xvoyltl9g8w">
                        {String(u.value).padStart(2, '0')}
                      </div>
                      <div className="text-[9px] md:text-[10px] lg:text-xs text-gray-400 uppercase tracking-[0.18em] font-bold" data-id="element-1192" data-mp="1rzttelsmjft0">
                        {u.label}
                      </div>
                    </div>)}
                </div>

                <button type="button" onClick={() => setShowModal(true)} className="group relative w-full max-w-md mx-auto bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0a1428] font-black text-base md:text-lg uppercase tracking-tight rounded-2xl py-5 md:py-6 px-6 shadow-[0_20px_60px_-15px_rgba(245,158,11,0.6)] transition-all duration-300 transform hover:scale-[1.02] overflow-hidden flex" data-id="element-1193" data-mp="1dyhdgadgre74">
                  <span className="relative z-10 flex items-center justify-center gap-2.5 w-full" data-id="element-1194" data-mp="3tm9yqo18ogeg">
                    <Ticket className="w-5 h-5 md:w-6 md:h-6" data-id="element-1195" data-mp="b8agpsp0k0t2" />
                    RESERVAR MI CUPO AHORA
                    <ArrowRight className="w-5 h-5 md:w-6 md:h-6 transition-transform group-hover:translate-x-1" data-id="element-1196" data-mp="6bae5rrdhg6p" />
                  </span>
                  <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-25deg] group-hover:animate-shine pointer-events-none" data-id="element-1197" data-mp="3es564c6mtg60" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ============ SECTION 6 — NO Es Para Todos ============ */}
        <section className="mb-16 md:mb-20 lg:mb-28" data-id="element-1198" data-mp="13wps858xs0zn">
          <div className="max-w-5xl mx-auto" data-id="element-1199" data-mp="yz6fxavstyac">
            <div className="text-center mb-10 lg:mb-12" data-id="element-1200" data-mp="yyekq16462kl">
              <div className="inline-block text-amber-400 text-xs md:text-sm font-black uppercase tracking-[0.2em] mb-3" data-id="element-1201" data-mp="2gw1gwerxp6no">
                Brutalmente Honesto
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight max-w-3xl mx-auto" data-id="element-1202" data-mp="2lt9s1veo0aff">
                Importante: Este Webinar{' '}
                <span className="text-amber-400" data-id="element-1203" data-mp="2cvr16aecb1vm">NO Es Para Todos</span>
              </h2>
            </div>

            <div className="space-y-4 mb-10 max-w-3xl mx-auto text-gray-300 text-base md:text-lg leading-relaxed text-center" data-id="element-1204" data-mp="30l25u2yt9gf1">
              <p data-id="element-1205" data-mp="34nyh18m0ua8y">
                Antes de que te registres, te quiero ser brutalmente honesto.
              </p>
              <p className="text-white font-semibold" data-id="element-1206" data-mp="2vtttoz63jgy6">
                Prefiero que te bajes ahora si no encajas, que perder 2 horas de
                tu tiempo (y del mío).
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6" data-id="element-1207" data-mp="3o9uvjhouas17">
              {/* Es para ti si */}
              <div className="bg-gradient-to-br from-amber-950/20 to-[#0a1428] border border-amber-500/30 rounded-2xl p-6 lg:p-8 shadow-[0_0_40px_rgba(245,158,11,0.1)]" data-id="element-1208" data-mp="20z4mfamgzvu">
                <div className="flex items-center gap-2.5 mb-5 lg:mb-6" data-id="element-1209" data-mp="3o7xb14iirpa1">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center" data-id="element-1210" data-mp="3oa0bkihat0t0">
                    <Sparkles className="w-4 h-4 text-amber-400" data-id="element-1211" data-mp="3f6z7sofwnlsi" />
                  </div>
                  <span className="text-amber-400 text-xs lg:text-sm font-black uppercase tracking-widest" data-id="element-1212" data-mp="3agitl249fw53">
                    Es Para Ti Si...
                  </span>
                </div>
                <ul className="space-y-3 lg:space-y-3.5" data-id="element-1213" data-mp="35f3a3ehqdxot">
                  {isFor.map(item => <li key={item} className="flex items-start gap-2.5" data-id="element-1214" data-mp="30k5x5whbz46n">
                      <CheckCircle2 className="w-4 h-4 lg:w-5 lg:h-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-1215" data-mp="2vhyiv0913c15" />
                      <span className="text-sm md:text-base lg:text-[17px] text-gray-200 leading-snug" data-id="element-1216" data-mp="2ri6ivbxpae7e">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>

              {/* No es para ti si */}
              <div className="bg-gradient-to-br from-red-950/20 to-[#0a1428] border border-red-500/20 rounded-2xl p-6 lg:p-8" data-id="element-1217" data-mp="1g00vew81dy41">
                <div className="flex items-center gap-2.5 mb-5 lg:mb-6" data-id="element-1218" data-mp="1ky2az7697far">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center" data-id="element-1219" data-mp="ncei2w90bf3z">
                    <XCircle className="w-4 h-4 text-red-400" data-id="element-1220" data-mp="sb97ih1hc1x2" />
                  </div>
                  <span className="text-red-400 text-xs lg:text-sm font-black uppercase tracking-widest" data-id="element-1221" data-mp="x910ledv1u2u">
                    No Es Para Ti Si...
                  </span>
                </div>
                <ul className="space-y-3 lg:space-y-3.5" data-id="element-1222" data-mp="4k3wqgaebztm">
                  {notFor.map(item => <li key={item} className="flex items-start gap-2.5" data-id="element-1223" data-mp="9joreeij9m0j">
                      <X className="w-4 h-4 lg:w-5 lg:h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-1224" data-mp="2b9dvjw8mu22o" />
                      <span className="text-sm md:text-base lg:text-[17px] text-gray-300 leading-snug" data-id="element-1225" data-mp="2b71ermem1xr7">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============ SECTION 7 — FAQ ============ */}
        <section className="mb-16 md:mb-20 lg:mb-28" data-id="element-1226" data-mp="2uwlor06fpc0t">
          <div className="max-w-3xl mx-auto" data-id="element-1227" data-mp="2uzhiecb91ejg">
            <div className="text-center mb-10 lg:mb-12" data-id="element-1228" data-mp="2pz3aqvdk86ly">
              <div className="inline-block text-amber-400 text-xs md:text-sm font-black uppercase tracking-[0.2em] mb-3" data-id="element-1229" data-mp="2q0mqmkvb0fn7">
                FAQ
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight" data-id="element-1230" data-mp="2z09sf7ry3ngs">
                Preguntas Que{' '}
                <span className="text-amber-400" data-id="element-1231" data-mp="19m4rq5ievxoc">Probablemente Tienes</span>
              </h2>
            </div>

            <div className="space-y-3 lg:space-y-4" data-id="element-1232" data-mp="14nbkuvxmr4x5">
              {faqs.map((f, idx) => <div key={f.q} className={`bg-white/[0.025] border rounded-2xl overflow-hidden transition-colors ${openFaq === idx ? 'border-amber-500/40' : 'border-white/[0.08]'}`} data-id="element-1233" data-mp="3g92neefjtu4e" data-mpi={idx}>
                  <button type="button" onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center gap-3 lg:gap-4 px-5 py-4 lg:px-7 lg:py-5 text-left hover:bg-white/[0.015] transition-colors" data-id="element-1234" data-mp="37fhal0my2mxm">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${openFaq === idx ? 'bg-amber-500/15 border border-amber-500/40' : 'bg-white/[0.04] border border-white/[0.08]'}`} data-id="element-1235" data-mp="2xjw00e3t8ec9">
                      <HelpCircle className={`w-4 h-4 lg:w-[18px] lg:h-[18px] ${openFaq === idx ? 'text-amber-400' : 'text-gray-400'}`} data-id="element-1236" data-mp="2si6sh8fsqisx" />
                    </div>
                    <span className="flex-1 text-white font-bold text-sm md:text-base lg:text-lg leading-snug" data-id="element-1237" data-mp="mnxb7aevtb1d">
                      {f.q}
                    </span>
                    <ArrowRight className={`w-4 h-4 lg:w-5 lg:h-5 text-gray-500 transition-transform flex-shrink-0 ${openFaq === idx ? 'rotate-90 text-amber-400' : ''}`} data-id="element-1238" data-mp="1zpv3x9lkae2v" />
                  </button>
                  {openFaq === idx && <div className="px-5 pb-5 lg:px-7 lg:pb-6 pl-[68px] lg:pl-[92px] text-gray-300 text-sm md:text-base lg:text-[17px] leading-relaxed" data-id="element-1239" data-mp="1qnvj067fepun">
                      {f.a}
                    </div>}
                </div>)}
            </div>
          </div>
        </section>

        {/* ============ SECTION 8 — FINAL CTA ============ */}
        <section className="mb-8" data-id="element-1240" data-mp="29ly449wus2n6">
          <div className="max-w-3xl mx-auto" data-id="element-1241" data-mp="29l6h3nhbnd8i">
            <div className="text-center mb-8 lg:mb-10" data-id="element-1242" data-mp="24neg83yp8gs6">
              <div className="inline-block text-amber-400 text-xs md:text-sm font-black uppercase tracking-[0.2em] mb-3" data-id="element-1243" data-mp="24mv8th6odase">
                Una Última Cosa
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight" data-id="element-1244" data-mp="2xbsiwx0qg1a2">
                Una Última Cosa Antes De Que{' '}
                <span className="text-amber-400" data-id="element-1245" data-mp="34pb772mztcdk">Te Vayas...</span>
              </h2>
            </div>

            <div className="space-y-4 mb-10 text-gray-300 text-base md:text-lg lg:text-[19px] leading-relaxed max-w-2xl mx-auto" data-id="element-1246" data-mp="39oe2vwh3268v">
              <p data-id="element-1247" data-mp="3imq38q7r4iei">Llegaste hasta el final de esta página.</p>
              <p data-id="element-1248" data-mp="3ioax0dm8rxbb">
                Eso me dice algo importante: algo en ti dice{' '}
                <span className="text-white font-semibold" data-id="element-1249" data-mp="3nljfkfbay3d3">"quiero más"</span>.
              </p>
              <p data-id="element-1250" data-mp="3sjky8z4tg0o1">
                La mayoría de los vendedores que aterrizan aquí NO se registran.
                Solo "miran". Se quedan pensando.
              </p>
              <p data-id="element-1251" data-mp="662z7j3w3u53">Y siguen con su vida en el dealership.</p>
              <p className="text-white font-semibold" data-id="element-1252" data-mp="67nu59u49f3h">
                Tú, que llegaste aquí, eres diferente. Y ahora solo te queda UNA
                decisión:
              </p>
            </div>

            <div className="relative bg-gradient-to-br from-[#0f1b35] to-[#0a1428] border-2 border-amber-500/40 rounded-3xl p-6 md:p-10 lg:p-12 shadow-[0_0_60px_rgba(245,158,11,0.15)] overflow-hidden" data-id="element-1253" data-mp="f46jyd71wmvu">
              <div className="absolute -inset-2 bg-amber-400/20 blur-3xl rounded-3xl pointer-events-none" data-id="element-1254" data-mp="vkh9zybldwr6" />
              <div className="relative" data-id="element-1255" data-mp="15gavw8b7b2lm">
                <div className="text-center mb-8" data-id="element-1256" data-mp="15hc7cdw4pr3v">
                  <Quote className="w-8 h-8 text-amber-400/40 mx-auto mb-4" data-id="element-1257" data-mp="15egbqw7r7eil" />
                  <p className="text-xl md:text-2xl lg:text-3xl font-black text-white leading-snug mb-3 max-w-xl mx-auto" data-id="element-1258" data-mp="hqzpobb30g03">
                    ¿El jueves a las 7 PM vas a estar viendo la Masterclass
                    conmigo?
                  </p>
                  <p className="text-gray-400 text-base lg:text-lg italic" data-id="element-1259" data-mp="mp9m0p8jlmna">
                    O vas a estar haciendo otra cosa que ya hiciste 1,000 veces.
                    <br data-id="element-1260" data-mp="3v3i6tb4tp94w" />
                    <span className="text-amber-400 font-bold not-italic" data-id="element-1261" data-mp="3q6rt2ughqpmn">
                      Tu decisión.
                    </span>
                  </p>
                </div>

                {/* Inline Registration Form */}
                <div className="max-w-md mx-auto bg-[#0a1428]/60 border border-amber-500/25 rounded-2xl p-5 md:p-6 backdrop-blur-sm" data-id="element-1262" data-mp="2ag93ll9g7lfb">
                  <div className="text-center mb-5" data-id="element-1263" data-mp="1vs4g83zgalq7">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-amber-500/10 border border-amber-500/30 rounded-2xl mb-3" data-id="element-1264" data-mp="20omlr62luo3l">
                      <Ticket className="w-5 h-5 text-amber-400" data-id="element-1265" data-mp="2ocw0xp5bcdlr" />
                    </div>
                    <h3 className="text-lg md:text-xl font-black text-white uppercase tracking-tight mb-1" data-id="element-1266" data-mp="2tda8z76wznjs">
                      Reserva Tu Cupo Gratis
                    </h3>
                    <p className="text-gray-400 text-xs md:text-sm" data-id="element-1267" data-mp="2jekqbc771z2h">
                      Masterclass EN VIVO — {thursdayLabel} · 7PM Miami
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3" data-id="element-1268" data-mp="181s0i6xyrkp2">
                    <div className="relative" data-id="element-1269" data-mp="aa9zh6x1k34r">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1270" data-mp="aai9mr1s4uc9">
                        <User className="h-4 w-4 text-gray-500" data-id="element-1271" data-mp="easo6s3n0t2q" />
                      </div>
                      <input ref={nameRef} type="text" name="name" value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre completo" required minLength={2} autoComplete="name" className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all" data-id="element-1272" data-mp="bkps1fatozl" />
                    </div>

                    <div className="relative" data-id="element-1273" data-mp="3dow4bbm3zv9v">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1274" data-mp="3iqv9mr1et29g">
                        <Mail className="h-4 w-4 text-gray-500" data-id="element-1275" data-mp="33tuh4lbe939x" />
                      </div>
                      <input ref={emailRef} type="email" name="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Tu mejor email" required autoComplete="email" className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all" data-id="element-1276" data-mp="38qkw15bgpstr" />
                    </div>

                    <div className="flex gap-2" data-id="element-1277" data-mp="2u45qt39ag8yv">
                      <div className="relative w-[38%]" data-id="element-1278" data-mp="384osztr2okt4">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none" data-id="element-1279" data-mp="3835fgawswnv1">
                          <Globe className="h-3.5 w-3.5 text-gray-500" data-id="element-1280" data-mp="342uyk37h0d1e" />
                        </div>
                        <select name="areaCode" value={areaCode} onChange={e => setAreaCode(e.target.value)} className="w-full pl-7 pr-1 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all appearance-none" data-id="element-1281" data-mp="3hwm7pahahuwl">
                          {COUNTRY_CODES.map(c => <option key={c.code} value={c.code} className="bg-gray-900" data-id="element-1282" data-mp="3twsywhwonrzr">
                              {c.code} {c.country}
                            </option>)}
                        </select>
                      </div>
                      <div className="relative w-[62%]" data-id="element-1283" data-mp="7ojk1fkeb6o4">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1284" data-mp="cgb0uydr1y12">
                          <Phone className="h-4 w-4 text-gray-500" data-id="element-1285" data-mp="hfnxubyc4lhd" />
                        </div>
                        <input ref={phoneRef} type="tel" name="phone" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} placeholder="WhatsApp (opcional)" autoComplete="tel" className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all" data-id="element-1286" data-mp="lgpo43a9xn77" />
                      </div>
                    </div>

                    <label className="flex items-start gap-2.5 cursor-pointer group pt-1" data-id="element-1287" data-mp="2x722ue8qtwru">
                      <div className="relative flex-shrink-0 mt-0.5" data-id="element-1288" data-mp="2pnsrwgkl9q0o">
                        <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} className="sr-only peer" data-id="element-1289" data-mp="2kvr889l3cx0m" />
                        <div className="w-4 h-4 border-2 border-gray-500 rounded bg-white/5 peer-checked:bg-amber-400 peer-checked:border-amber-400 transition-all flex items-center justify-center" data-id="element-1290" data-mp="2fxhd1yzd9wf0">
                          {smsConsent && <CheckCircle2 className="w-3 h-3 text-[#0a1428]" strokeWidth={3} data-id="element-1291" data-mp="38jsmlz4yb7ny" />}
                        </div>
                      </div>
                      <span className="text-[10px] md:text-[11px] text-gray-400 leading-snug" data-id="element-1292" data-mp="2ymwcjozemdha">
                        Acepto recibir SMS con recordatorios del webinar.
                        Responde STOP para cancelar.
                      </span>
                    </label>

                    <button type="submit" disabled={isSubmitting} className="group relative w-full bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0a1428] font-black text-sm md:text-base uppercase tracking-tight rounded-xl py-4 px-6 shadow-[0_0_30px_rgba(245,158,11,0.4)] transition-all duration-300 transform hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden mt-1" data-id="element-1293" data-mp="1i9pdiycu6j49">
                      <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-1294" data-mp="3251x6yb67pmn">
                        {isSubmitting ? <>Reservando...</> : <>
                            <Ticket className="w-4 h-4 md:w-5 md:h-5" data-id="element-1295" data-mp="3utr1t7bfqd35" />
                            RESERVAR MI CUPO AHORA
                            <ArrowRight className="w-4 h-4 md:w-5 md:h-5 transition-transform group-hover:translate-x-1" data-id="element-1296" data-mp="3fv5dz9ra6r4p" />
                          </>}
                      </span>
                      <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-25deg] group-hover:animate-shine pointer-events-none" data-id="element-1297" data-mp="24g9yjlkt8ln3" />
                    </button>

                    <div className="flex items-center justify-center gap-1.5 text-[10px] md:text-xs text-gray-500 pt-1" data-id="element-1298" data-mp="rgonrzjw1syn">
                      <Lock className="w-3 h-3" data-id="element-1299" data-mp="ml9ezm0qmn93" />
                      Tu información está 100% segura
                    </div>
                  </form>
                </div>

                <div className="mt-6 flex items-center justify-center gap-x-5 gap-y-2 flex-wrap text-xs md:text-sm text-gray-400" data-id="element-1300" data-mp="whug3w9mze7n">
                  <span className="flex items-center gap-1.5" data-id="element-1301" data-mp="8pe1xwt0s6dm">
                    <Calendar className="w-3.5 h-3.5 text-emerald-400" data-id="element-1302" data-mp="3u8egtjyyjnf" />
                    {thursdayLabel} · 7PM EST
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-1303" data-mp="ctdds23cuh0n">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" data-id="element-1304" data-mp="cs28fxu9h17f" />
                    Sin costo
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1305" data-mp="1k6xhlcadno0z" />

      {/* Registration Modal */}
      {showModal && <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setShowModal(false)} data-id="element-1306" data-mp="1k6nr7nlf7eur">
          <div className="bg-[#0f1b35] border border-amber-500/30 rounded-2xl max-w-md w-full p-6 md:p-8 relative shadow-[0_0_50px_rgba(245,158,11,0.2)] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-1307" data-mp="1tx0hzxjtw49c">
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors p-1" aria-label="Cerrar" data-id="element-1308" data-mp="1194rjakur8l0">
              <X className="w-6 h-6" data-id="element-1309" data-mp="1b6954m0mtvq7" />
            </button>

            <div className="text-center mb-6" data-id="element-1310" data-mp="1b4oc50tyukug">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-amber-500/10 border border-amber-500/30 rounded-2xl mb-3" data-id="element-1311" data-mp="1g52lqnb7u2ul">
                <Ticket className="w-6 h-6 text-amber-400" data-id="element-1312" data-mp="2mkwfv5l4w27p" />
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-white mb-2 uppercase tracking-tight" data-id="element-1313" data-mp="2rjg3rbjafo2p">
                Reservar Mi Cupo
              </h2>
              <p className="text-gray-400 text-sm" data-id="element-1314" data-mp="fyyum4xa1ivr">
                Masterclass EN VIVO — Este {thursdayLabel} 7PM Miami
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1315" data-mp="661eg5frc60b">
              <div className="space-y-1" data-id="element-1316" data-mp="18rjybdih45m">
                <label className="text-xs font-bold text-gray-400 uppercase ml-2" data-id="element-1317" data-mp="17q3frdq7pgi">
                  Nombre Completo
                </label>
                <div className="relative" data-id="element-1318" data-mp="3od7d2k634ta7">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1319" data-mp="3oc62egsze8t0">
                    <User className="h-5 w-5 text-gray-500" data-id="element-1320" data-mp="3jee9pkkiiinr" />
                  </div>
                  <input ref={nameRef} type="text" name="name" value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre" required minLength={2} autoComplete="name" className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all" data-id="element-1321" data-mp="3eft5zb61xguk" />
                </div>
              </div>

              <div className="space-y-1" data-id="element-1322" data-mp="qgm3q7p1sj8x">
                <label className="text-xs font-bold text-gray-400 uppercase ml-2" data-id="element-1323" data-mp="bs93gyixge8m">
                  Tu Mejor Email
                </label>
                <div className="relative" data-id="element-1324" data-mp="gqaohozwynmq">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1325" data-mp="gozidiizx3sg">
                    <Mail className="h-5 w-5 text-gray-500" data-id="element-1326" data-mp="2txdmypvxkh8" />
                  </div>
                  <input ref={emailRef} type="email" name="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ejemplo@correo.com" required autoComplete="email" className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all" data-id="element-1327" data-mp="7okx2j617ooy" />
                </div>
              </div>

              <div className="space-y-1" data-id="element-1328" data-mp="3cojjtubvvftv">
                <label className="text-xs font-bold text-gray-400 uppercase ml-2" data-id="element-1329" data-mp="3cpl6kw29hqrf">
                  WhatsApp (Para Recordatorios)
                </label>
                <div className="flex gap-2" data-id="element-1330" data-mp="3mecv0u845sxl">
                  <div className="relative w-1/3" data-id="element-1331" data-mp="3mfeglsmr9xtk">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none" data-id="element-1332" data-mp="3hnmqfj7ihu3y">
                      <Globe className="h-4 w-4 text-gray-500" data-id="element-1333" data-mp="3vf1cjywpubf8" />
                    </div>
                    <select name="areaCode" value={areaCode} onChange={e => setAreaCode(e.target.value)} className="w-full pl-8 pr-2 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all appearance-none text-sm" data-id="element-1334" data-mp="3rdg1mj3qs77o">
                      {COUNTRY_CODES.map(c => <option key={c.code} value={c.code} className="bg-gray-900" data-id="element-1335" data-mp="2ayo7a0n1cpvq">
                          {c.code} {c.country}
                        </option>)}
                    </select>
                  </div>

                  <div className="relative w-2/3" data-id="element-1336" data-mp="1eyjx0imbfru9">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1337" data-mp="1f04p81l5qyoy">
                      <Phone className="h-5 w-5 text-gray-500" data-id="element-1338" data-mp="16602nud249f8" />
                    </div>
                    <input ref={phoneRef} type="tel" name="phone" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} placeholder="Número" required minLength={7} autoComplete="tel" className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all" data-id="element-1339" data-mp="163nakodl4iog" />
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer group pt-1" data-id="element-1340" data-mp="i92olvvy7rov">
                <div className="relative flex-shrink-0 mt-0.5" data-id="element-1341" data-mp="2hlf0f0d3b5be">
                  <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} className="sr-only peer" data-id="element-1342" data-mp="2cm2d6eib3w8a" />
                  <div className="w-5 h-5 border-2 border-gray-500 rounded bg-white/5 peer-checked:bg-amber-400 peer-checked:border-amber-400 transition-all flex items-center justify-center" data-id="element-1343" data-mp="2k6cvo6o9q7cf">
                    {smsConsent && <CheckCircle2 className="w-3.5 h-3.5 text-[#0a1428]" strokeWidth={3} data-id="element-1344" data-mp="2twfq8bb1ftcv" />}
                  </div>
                </div>
                <span className="text-[11px] md:text-xs text-gray-400 leading-relaxed" data-id="element-1345" data-mp="2ywk5xz44gk21">
                  Acepto recibir mensajes SMS con recordatorios e información
                  del webinar. Puedo cancelar en cualquier momento respondiendo
                  STOP.
                </span>
              </label>

              <button type="submit" disabled={isSubmitting} className="group relative w-full bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0a1428] font-black text-base md:text-lg uppercase tracking-tight rounded-xl py-4 px-6 shadow-[0_0_30px_rgba(245,158,11,0.4)] transition-all duration-300 transform hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden" data-id="element-1346" data-mp="2f6i7wbzshxvl">
                <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-1347" data-mp="1milpetkjzexc">
                  {isSubmitting ? <>Reservando...</> : <>
                      <Ticket className="w-5 h-5" data-id="element-1348" data-mp="20fhbpnptf2vc" />
                      RESERVAR MI CUPO AHORA
                      <ArrowRight className="w-5 h-5" data-id="element-1349" data-mp="1vge6o39u4wnd" />
                    </>}
                </span>
              </button>

              <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500" data-id="element-1350" data-mp="13rb02guh7a7k">
                <Lock className="w-3 h-3" data-id="element-1351" data-mp="1hipnr0zbvzky" />
                Tu información está 100% segura
              </div>
            </form>
          </div>
        </div>}
    </div>;
}